import { useState } from "react"
import { FileText, Send, CheckCircle2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { toastSuccess, toastError } from "@/lib/toast"
import { useLanguage } from "@/hooks/useLanguage"

const Requirements = () => {
  const { t } = useLanguage()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    requirements: "",
  })

  const text = t.requirements

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Logic for sending requirements moved to backend
      console.log("Requirement submitted:", formData)

      toastSuccess(text.success.title)

      setFormData({
        name: "",
        email: "",
        company: "",
        requirements: "",
      })
    } catch (error) {
      toastError(text.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pb-20 pt-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <div className="mb-12 text-center">
              <h1 className="mb-4 font-display text-4xl uppercase tracking-tighter text-foreground md:text-6xl">
                {text.title}
              </h1>
              <p className="text-lg font-bold uppercase tracking-widest text-muted-foreground">
                {text.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 items-start gap-16 md:grid-cols-2">
              {/* Info Column */}
              <div className="space-y-8">
                <div className="prose prose-slate">
                  <p className="text-xl text-muted-foreground">
                    {text.description}
                  </p>
                </div>

                <div className="space-y-8 border-t border-foreground/5 pt-8">
                  <h3 className="flex items-center gap-3 font-display text-3xl uppercase tracking-widest">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                    {text.benefits.title}
                  </h3>
                  <ul className="space-y-6">
                    {text.benefits.items.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-4">
                        <CheckCircle2 className="mt-0.5 h-6 w-6 flex-shrink-0 text-primary" />
                        <span className="text-lg font-medium">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Form Column */}
              <Card className="card-industrial bg-muted p-1">
                <CardContent className="space-y-8 bg-background p-8 pt-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="text-xs font-black uppercase tracking-widest text-muted-foreground"
                      >
                        {text.form.name}
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-12 rounded-none border-input focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="text-xs font-black uppercase tracking-widest text-muted-foreground"
                      >
                        {text.form.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12 rounded-none border-input focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="company"
                        className="text-xs font-black uppercase tracking-widest text-muted-foreground"
                      >
                        {text.form.company}
                      </Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        required
                        className="h-12 rounded-none border-input focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="requirements"
                        className="text-xs font-black uppercase tracking-widest text-muted-foreground"
                      >
                        {text.form.requirements}
                      </Label>
                      <Textarea
                        id="requirements"
                        className="min-h-[150px] rounded-none border-input focus:border-primary"
                        placeholder={text.form.placeholder}
                        value={formData.requirements}
                        onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      className="h-16 w-full rounded-none bg-primary font-display text-xl tracking-widest text-primary-foreground shadow-lg hover:bg-primary/90"
                      disabled={loading}
                    >
                      <Send className="mr-2 h-5 w-5" />
                      {loading ? text.form.sending : text.form.submit}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Requirements

